/**
 * Cloche de notifications avec dropdown.
 * Monté dans la Sidebar. Refresh automatique toutes les 30 s (via le hook).
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { colors, fonts } from "../styles/tokens";
import useNotifications from "../hooks/useNotifications";
import { notificationsApi } from "../services/api";

export default function NotificationsBell() {
  const { count, items, loading, refresh } = useNotifications();
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const navigate = useNavigate();

  // Charge la liste à la première ouverture (et à chaque ouverture pour fraîcheur)
  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  // Click outside → ferme
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const onClickItem = async (notif) => {
    setOpen(false);
    if (!notif.lue) {
      try {
        await notificationsApi.lire(notif.id);
      } catch { /* silencieux */ }
    }
    refresh();
    // Routing par type
    if (notif.type === "nouvel_inscrit") {
      navigate("/annuaire?tab=en-attente");
    }
  };

  const onMarkAll = async (e) => {
    e.stopPropagation();
    try {
      await notificationsApi.lireTout();
      refresh();
    } catch { /* silencieux */ }
  };

  const badge = count > 9 ? "9+" : String(count);

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Notifications"
        title={count > 0 ? `${count} notification${count > 1 ? "s" : ""} non lue${count > 1 ? "s" : ""}` : "Aucune notification"}
        style={{
          position: "relative",
          width: 36,
          height: 36,
          borderRadius: 10,
          background: open ? colors.goldDim : colors.card,
          border: `1px solid ${open ? colors.goldBorder : colors.border}`,
          color: colors.text,
          fontSize: 18,
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "all 150ms",
        }}
      >
        🔔
        {count > 0 && (
          <span style={{
            position: "absolute",
            top: -4,
            right: -4,
            minWidth: 18,
            height: 18,
            padding: "0 5px",
            background: colors.rose,
            color: "#fff",
            fontSize: 10,
            fontWeight: 700,
            borderRadius: 999,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            border: `2px solid ${colors.surface}`,
            lineHeight: 1,
          }}>
            {badge}
          </span>
        )}
      </button>

      {open && (
        <div style={{
          position: "absolute",
          top: "calc(100% + 8px)",
          right: 0,
          width: 340,
          maxWidth: "calc(100vw - 32px)",
          background: colors.card,
          border: `1px solid ${colors.border}`,
          borderRadius: 12,
          boxShadow: "0 20px 50px rgba(0,0,0,0.55)",
          zIndex: 300,
          overflow: "hidden",
          fontFamily: fonts.body,
        }}>
          {/* Header */}
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "12px 14px",
            borderBottom: `1px solid ${colors.border}`,
            background: colors.surface,
          }}>
            <div style={{ fontWeight: 600, fontSize: 14, display: "inline-flex", alignItems: "center", gap: 8 }}>
              🔔 Notifications {count > 0 && (
                <span style={{ color: colors.rose, fontSize: 12, fontWeight: 700 }}>({count})</span>
              )}
            </div>
            {count > 0 && (
              <button
                type="button"
                onClick={onMarkAll}
                style={{
                  background: "transparent",
                  color: colors.gold,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Tout marquer
              </button>
            )}
          </div>

          {/* Liste */}
          <div style={{ maxHeight: 360, overflowY: "auto" }}>
            {loading && items.length === 0 ? (
              <div style={{ padding: 20, color: colors.textMuted, fontSize: 13, textAlign: "center" }}>
                Chargement…
              </div>
            ) : items.length === 0 ? (
              <div style={{ padding: 28, textAlign: "center", color: colors.textMuted }}>
                <div style={{ fontSize: 28, marginBottom: 6 }}>✓</div>
                <div style={{ fontSize: 13 }}>Aucune notification</div>
              </div>
            ) : (
              items.map((n) => (
                <NotifRow key={n.id} notif={n} onClick={() => onClickItem(n)} />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function NotifRow({ notif, onClick }) {
  const icon = ICONS[notif.type] || "🔔";
  const dot = !notif.lue;
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: "100%",
        textAlign: "left",
        padding: "12px 14px",
        background: dot ? colors.goldDim : "transparent",
        borderBottom: `1px solid ${colors.borderLight}`,
        cursor: "pointer",
        display: "flex",
        gap: 12,
        alignItems: "flex-start",
        transition: "background 150ms",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = dot ? colors.goldDim : colors.surface; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = dot ? colors.goldDim : "transparent"; }}
    >
      <span style={{ fontSize: 18, flexShrink: 0 }}>{icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, color: colors.text, lineHeight: 1.4 }}>{notif.message}</div>
        <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 4, display: "flex", justifyContent: "space-between", gap: 8 }}>
          <span>{relativeTime(notif.created_at)}</span>
          <span style={{ color: dot ? colors.rose : colors.green, fontWeight: 600 }}>
            {dot ? "● non lu" : "✓ lu"}
          </span>
        </div>
      </div>
    </button>
  );
}

const ICONS = {
  nouvel_inscrit: "🆕",
};

function relativeTime(iso) {
  if (!iso) return "récemment";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)}h`;
  return `il y a ${Math.floor(diff / 86400)} j`;
}
